import { Fieldset, Checkbox } from "@datapay-by-datacom/design-system";
import { useUserOrgs } from "../hooks/Org/useUserOrgs";
import { OrgKey, OrgKeyCategory } from "../../generated";
import { useAuthenticated } from "../../authn";
import { AsyncResult } from "../../shared/components/AsyncResult";
import s from "./OrgSelector.module.css";

// Helper function to deduplicate arrays by a key function
function deduplicateByKey<T>(arr: T[], keyFn: (item: T) => string): T[] {
  return arr.filter(
    (item, index, self) =>
      index === self.findIndex((t) => keyFn(t) === keyFn(item))
  );
}

/**
 * A controlled component for selecting organizations
 */
export interface OrgKeySelectorProps {
  value: OrgKey[];
  onChange: (value: OrgKey[]) => void;
  userOrgs: { id: string; category: OrgKeyCategory; name: string }[];
}

export function OrgKeySelector({
  value,
  onChange,
  userOrgs,
}: OrgKeySelectorProps) {
  // Helper to check if an org is selected
  const isOrgSelected = (orgKey: OrgKey) => {
    return value.some(
      (selectedOrg) =>
        selectedOrg.id === orgKey.id && selectedOrg.category === orgKey.category
    );
  };

  // Handle checkbox toggle
  const handleToggleOrg = (orgKey: OrgKey, checked: boolean) => {
    const newValue = checked
      ? deduplicateByKey(
          [...value, orgKey],
          ({ id, category }) => `${id}___${category}`
        )
      : value.filter(
          (org) => org.id !== orgKey.id || org.category !== orgKey.category
        );

    onChange(newValue);
  };

  // Group organizations by category
  const orgsByCategory = userOrgs.reduce<Record<string, typeof userOrgs>>(
    (grouped, org) => {
      const category = org.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(org);
      return grouped;
    },
    {}
  );

  return (
    <div className={s.categoriesContainer}>
      {Object.entries(orgsByCategory).map(([category, orgs]) => (
        <div key={category} className={s.categoryGroup}>
          <h3 className={s.categoryTitle}>{category}</h3>
          <div className={s.checkboxContainer}>
            {orgs.map((org) => {
              const checkboxKey = `${org.category}-${org.id}`;
              const orgKey: OrgKey = {
                id: org.id,
                category: org.category,
              };

              return (
                <Checkbox
                  key={checkboxKey}
                  value={checkboxKey}
                  isSelected={isOrgSelected(orgKey)}
                  onChange={(isSelected) => handleToggleOrg(orgKey, isSelected)}
                >
                  {org.name}
                </Checkbox>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export interface OrgSelectorProps {
  value: OrgKey[];
  onChange: (value: OrgKey[]) => void;
}

/**
 * A component that displays a list of organizations that the user is a member of
 * as toggle checkboxes, allowing selection of multiple organizations
 */
export function OrgSelector({ value, onChange }: OrgSelectorProps) {
  const { userId } = useAuthenticated();
  const userOrgsQuery = useUserOrgs(userId);

  return (
    <AsyncResult query={userOrgsQuery}>
      {(userOrgs) => (
        <Fieldset>
          <OrgKeySelector
            value={value}
            onChange={onChange}
            userOrgs={userOrgs}
          />
        </Fieldset>
      )}
    </AsyncResult>
  );
}
